import React from "react";
import { engineContributedNothing, describeIncludes } from "../../utils/nvaElimination";
import { projectedParentKpis } from "../../utils/subprocessRedesign";

// Shared render pieces for the NVA-elimination block, used by the RL and BPR
// redesign panels so an eliminated step reads identically in either place.
// Rendering only — the logic lives in utils/nvaElimination.js.

/**
 * Marks an applied heuristic row whose effect came from the CMS value-analysis
 * classification, not from the engine choosing it. Required on any row carrying
 * `nva_eliminated_task_ids`: without it the row is indistinguishable from one the
 * engine selected, and the engine gets credit for the analyst's work.
 */
export function NvaSourceChip() {
  return (
    <span
      title="These steps were classified non-value-adding in CMS and removed before the engine searched — the engine did not choose this."
      style={{
        display: "inline-block", fontSize: 9.5, fontWeight: 700, borderRadius: 999,
        padding: "1px 7px", whiteSpace: "nowrap", background: "var(--sky-tint-100)",
        color: "var(--sky-ink-800)", border: "1px solid var(--sky-tint-200)", cursor: "help",
      }}
    >
      ⌦ from CMS value analysis
    </span>
  );
}

function NameList({ items, render }) {
  return (
    <ul style={{ margin: "5px 0 0 16px", padding: 0 }}>
      {items.map((it, i) => (
        <li key={it.task_id ?? it.node_id ?? i} style={{ marginBottom: 3 }}>{render(it)}</li>
      ))}
    </ul>
  );
}

/**
 * The steps CMS classified non-value-adding and the structural fallout of removing
 * them. `dissolved_gateways` matters as much as the tasks: a gateway disappearing
 * changes the diagram and the branch list, and an analyst who only sees "1 task
 * removed" will not understand why the BPMN no longer matches.
 */
export function NvaEliminationCard({ nva, money, fmtMin, costKey = "resource_cost", costLabel = "Resource cost" }) {
  if (!nva) return null;

  const eliminated = Array.isArray(nva.eliminated) ? nva.eliminated : [];
  const skipped = Array.isArray(nva.skipped) ? nva.skipped : [];
  const gateways = Array.isArray(nva.dissolved_gateways) ? nva.dissolved_gateways : [];
  const post = nva.post_elimination || null;
  const engineOnly = nva.engine_only || null;
  const agentAddedNothing = engineContributedNothing(nva);

  return (
    <div style={{ marginBottom: 16, padding: "10px 14px", background: "var(--sky-tint-50)", border: "1px solid var(--sky-tint-200)", borderRadius: 10, color: "var(--sky-ink-800)", fontSize: 12.5 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 6 }}>
        <strong>Non-value-adding steps removed first</strong>
        <NvaSourceChip />
      </div>

      {eliminated.length > 0 && (
        <div>
          {eliminated.length} step{eliminated.length === 1 ? "" : "s"} classified non-value-adding in CMS
          {" "}{eliminated.length === 1 ? "was" : "were"} removed before the engine searched:
          <NameList items={eliminated} render={(t) => <strong>{t.task_name || `Task ${t.task_id}`}</strong>} />
        </div>
      )}

      {gateways.length > 0 && (
        <div style={{ marginTop: 8 }}>
          {gateways.length} gateway{gateways.length === 1 ? "" : "s"} dissolved as a result — the redesigned
          diagram no longer branches here:
          <NameList
            items={gateways}
            render={(g) => (
              <>
                <strong>{g.gateway_name || g.node_id}</strong>
                {g.reason ? <span style={{ color: "#0c74a8" }}> — {g.reason}</span> : null}
              </>
            )}
          />
        </div>
      )}

      {skipped.length > 0 && (
        <div style={{ marginTop: 8 }}>
          {skipped.length} flagged step{skipped.length === 1 ? "" : "s"} could not be removed:
          <NameList
            items={skipped}
            render={(t) => (
              <>
                <strong>{t.task_name || `Task ${t.task_id}`}</strong>
                {t.reason ? <span style={{ color: "#0c74a8" }}> — {t.reason}</span> : null}
              </>
            )}
          />
        </div>
      )}

      {post && (
        <div style={{ marginTop: 8, fontSize: 12 }}>
          After the removal, before the engine&apos;s own changes:{" "}
          <strong>{fmtMin(post.cycle_time ?? post.cycle_time_minutes)}</strong>
          {post[costKey] != null && (
            <> · <strong>{money(post[costKey])}</strong> {costLabel.toLowerCase()}</>
          )}
        </div>
      )}

      {/* Rule N2 — the headline percentage is the elimination's, not the agent's.
          Saying so is the difference between reporting a result and taking credit
          for one. */}
      {agentAddedNothing && (
        <div style={{ marginTop: 8, padding: "6px 10px", background: "var(--color-surface)", border: "1px solid var(--sky-tint-200)", borderRadius: 8, color: "var(--sky-ink-900)" }}>
          <strong>The engine itself found no further improvement.</strong> Everything in the
          headline above comes from removing these steps — the search added
          {engineOnly ? " 0.0%" : " nothing"} on top.
        </div>
      )}

      {nva.note && (
        <div style={{ marginTop: 8, fontSize: 11, color: "#0c74a8" }}>{nva.note}</div>
      )}
    </div>
  );
}

/**
 * One line stating what the headline percentage is measured from and what is baked
 * into it. Cheap to render and it removes the main way these panels can mislead —
 * a percentage whose basis the reader has to guess at.
 */
export function KpiBasisNote({ basis, headline }) {
  if (!basis) return null;
  const includes = describeIncludes(headline?.includes);
  return (
    <div style={{ marginBottom: 12, fontSize: 11.5, color: "var(--gray-500)" }}>
      Measured against {basis.as_is_means || "the stored process"}.
      {includes && <> The figures include {includes}.</>}
      {/* Where the saving actually comes from. A headline can read 72% because one
          step's queue collapsed, not because the process was restructured — and the
          two mean very different things for whether the number is achievable. The
          backend resolves this and sends one ready-to-render sentence; it gets real
          weight rather than grey small print, because it is the single line that
          stops a queue-driven figure being read as a redesign. */}
      {headline?.saving_note && (
        <div style={{ marginTop: 5, padding: "7px 10px", background: "var(--slate-50)", border: "1px solid var(--slate-200)", borderLeft: "3px solid var(--slate-500)", borderRadius: 6, color: "var(--slate-700)", fontSize: 12 }}>
          <strong style={{ color: "var(--slate-600)" }}>Where the saving comes from: </strong>
          {headline.saving_note}
        </div>
      )}
      {headline?.caveat && (
        <div style={{ marginTop: 3, color: "var(--amber-ink-700)" }}>{headline.caveat}</div>
      )}
      {/* Without this, an analyst who opens the Comparison tab next sees a much
          smaller improvement for the same engine and concludes one of the two pages
          is broken. cross_engine_note is the backend saying which scale is which. */}
      {basis.cross_engine_note && (
        <details style={{ marginTop: 5 }}>
          <summary style={{ cursor: "pointer", fontWeight: 700, color: "var(--amber-ink-700)" }}>
            Why these figures differ from the Comparison report
          </summary>
          <div style={{ marginTop: 4, padding: "6px 10px", background: "var(--amber-tint-50)", border: "1px solid var(--amber-tint-200)", borderRadius: 8, color: "var(--amber-ink-800)" }}>
            {basis.cross_engine_note}
            {headline?.includes?.includes("projected_subprocess_redesigns") && (
              <div style={{ marginTop: 4 }}>
                The Comparison report also scores every engine on this process alone: it runs the
                sub-process redesigns but does not price them in, so the improvement it shows for
                this engine is the smaller, parent-only one.
              </div>
            )}
          </div>
        </details>
      )}
    </div>
  );
}

/**
 * Splits a compounded headline into the parts it was built from. Without this the
 * reader sees one percentage and attributes all of it to the engine named on the
 * tab, when a large share of it can be the CMS elimination.
 *
 * `subprocessProjection` is separate from `combined.components` because the backend
 * keeps it separate: `combined_improvement` is computed before the sub-process flag
 * is even considered and never gains a third component when it's on — the flag only
 * changes `kpi_basis.headline` and adds `subprocess_redesign_projection`. Without this
 * prop the reader sees two different percentages (this table's total and the larger
 * headline badge above it) with nothing here to explain the gap.
 */
export function CombinedImprovementBreakdown({ combined, labels = {}, subprocessProjection }) {
  const components = combined?.components;
  const subKpis = projectedParentKpis(subprocessProjection);
  const subRow = subKpis?.cycle_time ? {
    key: "subprocess_redesigns",
    label: "Sub-process redesigns (projected)",
    time: subKpis.cycle_time.reduction_pct,
    cost: subKpis.resource_cost?.reduction_pct,
    measuredBy: "rolled up from the sub-process redesigns, projected",
    isProjection: true,
  } : null;

  if (!components && !subRow) return null;

  const rows = [
    ...Object.entries(components || {}).map(([key, c]) => ({
      key,
      label: labels[key] || String(key).replace(/_/g, " "),
      time: c?.cycle_time_reduction_pct,
      cost: c?.labor_cost_reduction_pct ?? c?.resource_cost_reduction_pct,
      measuredBy: c?.measured_by,
    })),
    ...(subRow ? [subRow] : []),
  ].filter((r) => r.time != null || r.cost != null);

  if (rows.length === 0) return null;

  const pct = (v) => (v == null ? "—" : `${v >= 0 ? "" : "−"}${Math.abs(v).toFixed(1)}%`);

  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: "var(--gray-500)", textTransform: "uppercase", marginBottom: 5 }}>
        What the headline is made of
      </div>
      <div style={{ overflowX: "auto" }}>
      <table style={{ borderCollapse: "collapse", fontSize: 12, minWidth: 420 }}>
        <thead>
          <tr>
            {["Component", "Cycle time", "Cost", "Measured by"].map((h, i) => (
              <th key={h} style={{ padding: "4px 10px", fontSize: 10, color: "var(--gray-500)", textAlign: i === 0 || i === 3 ? "left" : "right", textTransform: "uppercase", borderBottom: "1.5px solid var(--gray-200)" }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key} style={r.isProjection ? { background: "var(--purple-tint-50)" } : undefined}>
              <td style={{ padding: "4px 10px", fontWeight: 600, color: r.isProjection ? "var(--violet-ink-800)" : "inherit" }}>
                {r.label}
              </td>
              <td style={{ padding: "4px 10px", textAlign: "right" }}>{pct(r.time)}</td>
              <td style={{ padding: "4px 10px", textAlign: "right" }}>{pct(r.cost)}</td>
              <td style={{ padding: "4px 10px", color: "var(--gray-400)", fontSize: 11 }}>
                {String(r.measuredBy || "").replace(/_/g, " ") || "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
      {/* The components are measured on different bases and compounded, so the
          headline is not their sum and is not (as_is − to_be) / as_is either.
          Left unsaid, a reader who checks the arithmetic concludes the page is
          wrong. */}
      <div style={{ marginTop: 5, fontSize: 10.5, color: "var(--gray-400)", maxWidth: 620 }}>
        These are measured on different baselines and compound rather than add, so the headline
        percentage is neither their sum nor the ratio of the AS-IS and TO-BE figures below.
        {subRow && (
          <> The sub-process row is a projection — those child redesigns are proposals and are not saved in the CMS.</>
        )}
      </div>
    </div>
  );
}
